/**
 * Spatial clustering using Supercluster.
 * Separate indices per layer group to avoid mixing unrelated data.
 * Renders clusters as ScatterplotLayer (radius ~ point_count) + TextLayer (count label).
 */

import Supercluster from 'supercluster';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent, Severity } from '@/sources/base-source';
import { SEVERITY_RANK } from '@/utils/severity';
import { SEVERITY_COLORS } from '@/map/layer-colors';

/** Properties stored on each point feature in the cluster index. */
interface PointProps {
  eventId: string;
  severity: Severity;
  riot: boolean;
  event: NormalizedEvent;
}

/** Properties accumulated across clusters. */
interface ClusterProps {
  maxSeverity: Severity;
  count: number;
  riotCount: number;
}

const SEVERITY_COLOR: Record<Severity, [number, number, number]> = SEVERITY_COLORS;

/**
 * Cluster fill alpha descends with severity so calmer clusters visually
 * recede (WM DeckGLMap.ts:4126-4131's scheme applied to the generic path).
 */
const SEVERITY_CLUSTER_ALPHA: Record<Severity, number> = {
  critical: 200, high: 180, medium: 160, low: 140, info: 140,
};

function higherSeverity(a: Severity, b: Severity): Severity {
  return SEVERITY_RANK[a] >= SEVERITY_RANK[b] ? a : b;
}

/** ACLED tags carry the event_type verbatim — 'Riots' marks riot events. */
export function isRiotEvent(event: NormalizedEvent): boolean {
  return event.tags?.some((t) => t.toLowerCase() === 'riots' || t.toLowerCase() === 'riot') ?? false;
}

/** Layer group definitions for clustering isolation. */
export type ClusterGroup = 'conflicts' | 'protests' | 'environmental' | 'maritime' | 'aviation' | 'economic' | 'infrastructure';

/**
 * Per-domain Supercluster radii — WM tunes these per data type
 * (protests 60, DeckGLMap.ts:1482-1484); others use the generic default.
 */
export const GROUP_CLUSTER_RADIUS: Partial<Record<ClusterGroup, number>> = {
  protests: 60,
};

/** Map layer IDs to cluster groups. */
/**
 * Only layers that render through the generic ICON_LAYER_IDS or POINT_LAYER_IDS
 * paths belong here. Layers with specialized renderers (MILITARY_BASE_LAYER_IDS,
 * CYBER_LAYER_IDS, INFRA_STATUS_LAYER_IDS, TRACK_LAYER_IDS, etc.) must NOT be
 * included — they already render their own visuals and adding them here causes
 * double-rendering (inflated cluster counts + phantom grey circles).
 */
const LAYER_GROUP_MAP: Record<string, ClusterGroup> = {
  // ICON_LAYER_IDS: conflicts, protests, embassies, cyber-incidents, power-plants, telecom-towers
  'conflicts': 'conflicts', 'embassies': 'conflicts',
  'refugee-camps': 'conflicts', 'humanitarian-ops': 'conflicts',
  'sanctions-markers': 'conflicts',
  // Protests cluster in isolation (WM keeps a dedicated index, radius 60)
  // so a protest cluster count means protests, not mixed conflict events
  'protests': 'protests', 'protests-cluster': 'protests',
  'nasa-events': 'environmental',
  'ai-datacenters': 'economic', 'spaceports': 'economic',
  'cyber-incidents': 'infrastructure', 'power-plants': 'infrastructure',
  'telecom-towers': 'infrastructure',
};

export function getClusterGroup(layerId: string): ClusterGroup | undefined {
  return LAYER_GROUP_MAP[layerId];
}

/** Wrapper around a Supercluster index for a single layer group. */
export interface ClusterIndex {
  getClusters(bbox: [number, number, number, number], zoom: number): ClusterResult[];
  getExpansionZoom(clusterId: number): number;
  pointCount: number;
}

export interface ClusterResult {
  isCluster: boolean;
  lng: number;
  lat: number;
  pointCount: number;
  maxSeverity: Severity;
  /** Whether the cluster contains at least one riot event (ACLED). */
  hasRiot: boolean;
  clusterId?: number;
  event?: NormalizedEvent;
}

/**
 * Create a Supercluster index from a set of events.
 * When content clusters are available, uses a larger spatial radius
 * to merge semantically related events that are geographically near each other.
 */
export function createClusterIndex(
  events: NormalizedEvent[],
  contentClusters?: Map<string, string>,
  radiusOverride?: number,
): ClusterIndex {
  const geoEvents = events.filter((e) => e.location);

  const points: Supercluster.PointFeature<PointProps>[] = geoEvents.map((e) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [e.location!.lng, e.location!.lat],
    },
    properties: {
      eventId: e.id,
      severity: e.severity ?? 'info',
      riot: isRiotEvent(e),
      event: e,
    },
  }));

  // Use a larger radius when content clusters are available —
  // semantically related events get merged over a wider area
  const hasContentClusters = contentClusters && contentClusters.size > 0;
  const index = new Supercluster<PointProps, ClusterProps>({
    radius: radiusOverride ?? (hasContentClusters ? 50 : 40),
    maxZoom: 14,
    minPoints: 2,
    map: (props) => ({
      maxSeverity: props.severity,
      count: 1,
      riotCount: props.riot ? 1 : 0,
    }),
    reduce: (accumulated, props) => {
      accumulated.maxSeverity = higherSeverity(accumulated.maxSeverity, props.maxSeverity);
      accumulated.count += props.count;
      accumulated.riotCount += props.riotCount;
    },
  });

  index.load(points);

  return {
    pointCount: geoEvents.length,
    getClusters(bbox, zoom) {
      const features = index.getClusters(bbox, zoom);
      return features.map((f) => {
        const coords = f.geometry.coordinates;
        const props = f.properties;

        if ('cluster' in props && props.cluster) {
          return {
            isCluster: true,
            lng: coords[0],
            lat: coords[1],
            pointCount: props.point_count,
            maxSeverity: props.maxSeverity,
            hasRiot: props.riotCount > 0,
            clusterId: props.cluster_id,
          };
        }

        return {
          isCluster: false,
          lng: coords[0],
          lat: coords[1],
          pointCount: 1,
          maxSeverity: (props as PointProps).severity,
          hasRiot: (props as PointProps).riot,
          event: (props as PointProps).event,
        };
      });
    },
    getExpansionZoom(clusterId) {
      return index.getClusterExpansionZoom(clusterId);
    },
  };
}

/** Threshold: use clustering when event count exceeds this. */
const CLUSTER_THRESHOLD = 30;

/** Check if clustering should be applied for a given set of events. */
export function shouldCluster(events: NormalizedEvent[]): boolean {
  return events.length > CLUSTER_THRESHOLD;
}

/**
 * WM's protest cluster fill (DeckGLMap.ts:4126-4131): riot red beats
 * severity, and alpha descends with severity.
 */
export function getProtestClusterFill(cluster: ClusterResult): [number, number, number, number] {
  if (cluster.hasRiot) return [220, 40, 40, 200];
  if (cluster.maxSeverity === 'critical' || cluster.maxSeverity === 'high') return [255, 80, 60, 180];
  if (cluster.maxSeverity === 'medium') return [255, 160, 40, 160];
  return [255, 220, 80, 140];
}

/**
 * Pulse ring behind hot protest clusters (WM DeckGLMap.ts:4155-4173).
 * Pulse phase is sampled from Date.now() at build, so the caller must
 * build this FRESH on every rebuild — never serve it from the layer
 * cache, where the phase would freeze mid-cycle. Returns null for
 * non-protest groups and when no cluster is hot.
 */
export function createClusterPulseLayer(
  groupId: string,
  clusters: ClusterResult[],
): Layer | null {
  if (groupId !== 'protests') return null;

  const hotClusters = clusters.filter(
    (c) => c.isCluster && (c.hasRiot || c.maxSeverity === 'high' || c.maxSeverity === 'critical'),
  );
  if (hotClusters.length === 0) return null;

  const pulse = 1.0 + 0.8 * (0.5 + 0.5 * Math.sin(Date.now() / 400));
  return new ScatterplotLayer<ClusterResult>({
    id: `cluster-pulse-${groupId}`,
    data: hotClusters,
    pickable: false,
    filled: false,
    stroked: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getRadius: (d) => 15_000 + d.pointCount * 2000,
    radiusScale: pulse,
    radiusMinPixels: 8,
    radiusMaxPixels: 30,
    lineWidthMinPixels: 1.5,
    getLineColor: (d) => d.hasRiot
      ? [220, 40, 40, 120] as [number, number, number, number]
      : [255, 80, 60, 100] as [number, number, number, number],
  });
}

/** Render cluster features as deck.gl layers (ScatterplotLayer for dots + TextLayer for counts).
 *  The protest pulse ring lives in createClusterPulseLayer — built per rebuild, never cached. */
export function createClusterLayers(
  groupId: string,
  clusters: ClusterResult[],
  _zoom: number,
): Layer[] {
  const clusterFeatures = clusters.filter((c) => c.isCluster);
  if (clusterFeatures.length === 0) return [];

  const isProtests = groupId === 'protests';
  const layers: Layer[] = [];

  // Cluster dots — meters so clusters grow on zoom-in (WM DeckGLMap.ts:4119-4134);
  // no layer opacity, the per-severity alpha carries the fade
  layers.push(new ScatterplotLayer<ClusterResult>({
    id: `cluster-dots-${groupId}`,
    data: clusterFeatures,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => isProtests
      ? getProtestClusterFill(d)
      : [...SEVERITY_COLOR[d.maxSeverity], SEVERITY_CLUSTER_ALPHA[d.maxSeverity]],
    getRadius: (d) => 15_000 + d.pointCount * 2000,
    radiusMinPixels: 6,
    radiusMaxPixels: 22,
  }));

  // Count labels — matching WorldMonitor badge style
  layers.push(new TextLayer<ClusterResult>({
    id: `cluster-labels-${groupId}`,
    data: clusterFeatures.filter((c) => c.pointCount > 1),
    getPosition: (d) => [d.lng, d.lat],
    getText: (d) => String(d.pointCount),
    getSize: 12,
    getColor: [255, 255, 255, 255],
    getTextAnchor: 'middle' as const,
    getAlignmentBaseline: 'center' as const,
    fontFamily: 'system-ui, sans-serif',
    fontWeight: 700,
    sizeUnits: 'pixels' as const,
    // background must be explicitly enabled or the badge plate never draws
    background: true,
    getBackgroundColor: [0, 0, 0, 180],
    backgroundPadding: [4, 2, 4, 2],
    getPixelOffset: [0, -14],
  }));

  return layers;
}
